import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ToastService } from 'projects/shared/core/services/toast.service';
import { loadModules } from 'esri-loader';
import esri = __esri;

export type EsriMarkerData = {
  latitude: number,
  longitude: number,
  title: string,
  address: string,
  cityStateZip: string,
  enroute: number,
  onsite: number,
  staged: number,
  billeting: number,
  url: string
};

@Component({
  selector: 'map-esri',
  templateUrl: './map-esri.component.html',
  styles: ['@import "https://js.arcgis.com/4.12/esri/css/main.css"'],
  styleUrls: ['./map-esri.component.scss']
})
export class MapEsriComponent implements OnInit, OnChanges {
  @Input() set markers(value: EsriMarkerData[]) {
    if (value) {
      this.locationMarkers = value;
    }
  }
  @Input() set zoom(value: string) {
    if (value) {
      this.mapZoom = +value;
    }
  };
  @Input() basemap: string = 'streets';
  @Input() center = undefined;
  @Input() popup: boolean = false;
  @Input() popupContent: string = "";

  @Output() mapLoaded = new EventEmitter<boolean>();
  
  locationMarkers: EsriMarkerData[] = undefined;
  mapZoom: number = 15;
  map: esri.Map;
  mapView: esri.MapView;
  graphics: esri.Graphic[];
  graphicsLayer: esri.GraphicsLayer;
  markerBase: string;

  constructor(
    @Inject('BASE_URL') baseUrl: string,
    private _toastService: ToastService) {
      this.markerBase = baseUrl;
    }

  ngOnInit(): void {
    this.buildMap();
    this.buildMapView();
    this.addGraphicsLayer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["markers"].firstChange) return;
    let check = require('lodash');
    if (!check.isEqual(changes["markers"].previousValue, changes["markers"].currentValue) && this.map) {
      this.map.remove(this.graphicsLayer);
      this.calculateCenter();
      this.mapView.center = this.center;
      this.addGraphicsLayer();
    }
  }

  private addGraphicsLayer() {
    loadModules([
      'esri/geometry/Point',
      'esri/Graphic',
      'esri/layers/GraphicsLayer',
      'esri/PopupTemplate',
      'esri/symbols/PictureMarkerSymbol'
    ]).then(([Point, Graphic, GraphicsLayer, PopupTemplate, PictureMarkerSymbol]) => {
      this.graphicsLayer = new GraphicsLayer;
      this.map.add(this.graphicsLayer);
      
      let popupTemplate = undefined;
      if (this.popup) {
        popupTemplate = new PopupTemplate({
          content: this.popupContent,
          title: '{popup_title}'
        });
      }
      
      let markerSymbol = new PictureMarkerSymbol({
        url: this.markerBase + '/assets/esri-marker.png',
        width: '30px',
        height: '30px'
      });

      this.locationMarkers.forEach(marker => {
        let graphic = new Graphic({
          attributes: {
            popup_title: marker.title,
            popup_address: marker.address,
            popup_city_state_zip: marker.cityStateZip,
            popup_enroute: marker.enroute,
            popup_onsite: marker.onsite,
            popup_staged: marker.staged,
            popup_billeting: marker.billeting,
            popup_url: marker.url
          },
          geometry: new Point({
            latitude: marker.latitude,
            longitude: marker.longitude,
          }),
          popupTemplate: popupTemplate,
          symbol: markerSymbol
        })
        this.graphicsLayer.add(graphic);
      });
    })
  }

  private buildMap() {
    loadModules([
      'esri/Map'
    ]).then(([EsriMap]) => {
      this.map = new EsriMap({
        basemap: this.basemap
      });
    })
  }

  private buildMapView() {
    loadModules([
      'esri/views/MapView'
    ]).then(([EsriMapView]) => {
      if (!this.center) {
        this.calculateCenter();
      }
      this.mapView = new EsriMapView({
        map: this.map,
        center: this.center,
        zoom: this.mapZoom,
        container: "mapViewDiv"
      });
    })
  }  

  private calculateCenter() {
    let latitude: number = 0.0;
    let longitude: number = 0.0;
    this.locationMarkers.forEach(marker => {
      latitude += +marker.latitude;
      longitude += +marker.longitude;
    });
    this.center = {
      latitude: latitude / this.locationMarkers.length,
      longitude: longitude / this.locationMarkers.length
    }
  }

}